import type { ChangeEvent, MouseEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { useDebounce } from "../../../../../../util/hooks";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  defaultHandleSort,
  DEFAULT_PER_PAGE,
  useStoreState,
} from "../../../../../../util/util";
import { useRoutePath } from "../../../../../../util/Routing";
import type { SortingRule } from "react-table";
import useSWR from "swr";
import { endpoints } from "../../../../../../endpoints";
import { PageWrapper } from "../../../../../../layout/portalPageLayout";
import { AttributesNav } from "../../components/AttributesNav";
import { SlideOut } from "../../../../../../components/SlideOut/SlideOut";
import { createChangePerPageHandler } from "../../../../../../components/Pagination/util";
import { TablePageLayout } from "../../../../../../components/TablePage/TablePageLayout";
import type { CollectionPaginatedOutputSchema } from "../../../../../../types/types.PIM";
import { PrimaryButtonWithPlusIcon } from "../../../../../../components/Buttons/Buttons";
import { SellerAdminAttributesCollectionForm } from "./SellerAdminAttributesCollectionForm";
import type { AxiosError } from "axios";
import { useAuthContext } from "../../../../../../components/Auth";
import { NameWithSystemDefaultIcon } from "../../components/PIM.components.util";
import type { Getter } from "@tanstack/react-table";

export const SellerAdminAttributesCollectionsList = () => {
  const [tableData, setTableData] = useState<
    {
      id: string;
      name: { name: string; id: string; is_system: boolean };
      attribute_count: number;
      display_name: string;
    }[]
  >([]);
  const perPageItems = [10, 20, 50];
  const [query, setQuery] = useQueryParams({
    q: StringParam,
    offset: NumberParam,
    perPage: NumberParam,
  });
  const [perPage, setPerPage] = useState(query?.perPage ?? DEFAULT_PER_PAGE);
  const [tablePagination, setTablePagination] = useState({
    perPage: perPage,
    pageCount: 0,
    pageIndex: 0,
  });
  const [offset, setOffset] = useState(query?.offset ?? 0);
  const [showCreateSlideOut, setCreateSlideOut] = useState(false);
  const [searchQuery, setSearchQuery] = useState(query.q || "");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    orderBy: "asc",
  });
  const history = useHistory();
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { adminPath } = useRoutePath();
  const handleSort = async (rules: SortingRule<object>[]) =>
    defaultHandleSort(rules, sortingRules, setSortingRules, setTableData);

  const { hasPermission } = useAuthContext();
  const tableColumns = useMemo(
    () => [
      {
        header: t("Collection Name"),
        accessorKey: "name",
        cell: ({
          getValue,
        }: {
          getValue: Getter<{ name: string; is_system: boolean; id: string }>;
        }) => (
          <NameWithSystemDefaultIcon
            name={getValue().name}
            id={getValue().id}
            is_system={getValue().is_system}
          />
        ),
      },
      {
        header: t("Display Name"),
        accessorKey: "display_name",
      },
      {
        header: t("No. of Attributes"),
        accessorKey: "attribute_count",
      },
    ],
    [t]
  );

  const constructQuery = ({
    baseUrl,
    query,
  }: {
    baseUrl: string;
    query: string;
  }) => {
    const paramsWithOffset = new URLSearchParams(
      `offset=${offset}&limit=${perPage}`
    );
    if (query) paramsWithOffset.append("q", query);
    paramsWithOffset.append("order_by", sortingRules.orderBy || "asc");
    if (sortingRules.sortBy) {
      paramsWithOffset.append("sort_by", sortingRules.sortBy);
    }
    return baseUrl + paramsWithOffset;
  };

  const {
    data: collectionsList,
    error: collectionsError,
    mutate,
  } = useSWR<CollectionPaginatedOutputSchema, AxiosError>(
    constructQuery({
      baseUrl: `${endpoints.v2_tenants_id_pim_collections(tenant_id)}?`,
      query: debouncedSearchQuery,
    })
  );

  const isLoading = !collectionsList && !collectionsError;

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setOffset(0);
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setQuery({ q: undefined });
    setOffset(0);
  };

  const handleRowClick = (e: MouseEvent) => {
    const params = new URLSearchParams();
    if (query.q) {
      params.append("q", query.q);
    }
    params.append("offset", String(offset));
    params.append("perPage", String(perPage));
    // TODO: update to the route
    history.push(
      `${adminPath}/pim/attributes/collections/${e.currentTarget.id}?${params}`
    );
  };

  const closeCreateSlideOut = () => setCreateSlideOut(false);

  const changePage = (offset: number) => {
    setOffset(offset);
    setTableData([]);
  };

  const changePerPage = createChangePerPageHandler(
    offset,
    setPerPage,
    setOffset
  );

  const onCollectionCreationSuccess = () => {
    mutate();
    closeCreateSlideOut();
  };

  useEffect(() => {
    const handleData = ({
      data,
      pagination,
    }: CollectionPaginatedOutputSchema) => {
      setTableData(
        data.map(({ id, name, attribute_count, display_name, is_system }) => ({
          id,
          name: { name: name, id: id, is_system: is_system },
          attribute_count,
          display_name: display_name ? t([display_name]) : "",
        }))
      );
      setTablePagination({
        perPage: perPage,
        pageCount: Math.ceil(pagination.total / perPage),
        pageIndex: pagination.offset / perPage + 1,
      });
    };

    if (collectionsList) {
      const { data: collections, pagination } = collectionsList;
      handleData({ data: collections, pagination });
    }
  }, [collectionsList, perPage, t]);

  useEffect(() => {
    setQuery({ q: debouncedSearchQuery || undefined });
  }, [setQuery, debouncedSearchQuery]);

  return (
    <PageWrapper>
      <AttributesNav tabName={"Collections"} />
      <SlideOut
        closeFlyout={closeCreateSlideOut}
        show={showCreateSlideOut}
        title={t("Create Collection")}
      >
        <SellerAdminAttributesCollectionForm
          onSuccess={onCollectionCreationSuccess}
          onCancel={closeCreateSlideOut}
        />
      </SlideOut>
      <TablePageLayout
        showTitle={false}
        searchQuery={searchQuery}
        searchPlaceholder={t("Search by Collection name or Attribute name")}
        onSearchChange={handleSearch}
        onSearchClear={handleClearSearch}
        toolbarActions={
          hasPermission("modify_collections") ? (
            <PrimaryButtonWithPlusIcon
              onClick={() => setCreateSlideOut(true)}
              data-alignright
              useV2
            >
              <span style={{ fontSize: "15px" }}>{t("Create")}</span>
            </PrimaryButtonWithPlusIcon>
          ) : undefined
        }
        perPageItems={perPageItems}
        perPage={perPage}
        onPerPageChange={changePerPage}
        columns={tableColumns}
        data={tableData}
        isLoading={isLoading}
        error={collectionsError}
        rowClick={handleRowClick}
        handleSort={handleSort}
        lastChildleftAlign
        pagination={tablePagination}
        offset={offset}
        totalCount={collectionsList?.pagination?.total}
        onPageChange={changePage}
      />
    </PageWrapper>
  );
};
